/* eslint-disable no-nested-ternary */
import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import DropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import {
  fetchPublicVBTData,
  setSelectedEndDateAnalysis,
  setSelectedExercise,
} from '../../ducks/vbtDashboardActions';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import VBTAnalysisTableNoData from './VBTAnalysisTableNoData';
import Text from '../../../../shared/_TBComponents/Text';
import Icon from '../../../../shared/_TBComponents/Icon';
import ThresholdFilter from '../ThresholdFilter';
import VBTAnalysisTable from './VBTAnalysisTable';

const ColorNote = styled('div')`
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: normal;
`;

const DashboardContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 15px 15px 7px 15px;
  padding: 16px;
  position: relative;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const NoDataModal = styled('div')`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  color: #424242;
  font-family: 'Nunito Sans';
  font-size: 19px;
  height: 119px;
  left: 50%;
  margin: -45px 0px 0px -182px;
  padding: 15px;
  position: absolute;
  text-align: center;
  top: 50%;
  width: 400px;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SpinnerContainer = styled('div')`
  height: 550px;
`;

const SubtitleContainer = styled('div')`
  display: flex;
`;

const ViewOnlyAnalysis = ({ token }) => {
  const endDateData = useSelector((state) => state.vbtDashboard.data.endDateData);
  const exercises = useSelector((state) => state.vbtDashboard.data.exercises);
  const group = useSelector((state) => state.vbtDashboard.data.group);
  const isVBTDataLoading = useSelector((state) => state.vbtDashboard.ui.isVBTDataLoading);
  const selectedExercise = useSelector((state) => state.vbtDashboard.data.selectedExercise);
  const selectedEndDateAnalysis = useSelector(
    (state) => state.vbtDashboard.data.selectedEndDateAnalysis,
  );
  const startDate = useSelector((state) => state.vbtDashboard.data.startDate);
  const tableThreshold = useSelector((state) => state.vbtDashboard.data.tableThreshold);
  const vbtData = useSelector((state) => state.vbtDashboard.data.vbtData);

  const dispatch = useDispatch();

  const datesWithData = vbtData?.tableData?.dates.map((item) => parseISO(item.date));
  const groupData = vbtData?.tableData?.dates.find(
    (day) => day.date === format(new Date(selectedEndDateAnalysis), 'yyyy-MM-dd'),
  )?.groupData;
  const tableData = vbtData?.tableData?.dates.find(
    (day) => day.date === format(new Date(selectedEndDateAnalysis), 'yyyy-MM-dd'),
  )?.athleteData;

  const hasTableData = tableData?.length > 0;

  const handleEndDate = (date) => {
    dispatch(setSelectedEndDateAnalysis(new Date(date)));
  };

  const setExercise = (exer) => {
    if (!exer.id) return;
    batch(() => {
      dispatch(setSelectedExercise(true, exer));
      dispatch(fetchPublicVBTData(exer.id, token));
    });
  };

  const emptyFunction = () => undefined;

  return (
    <>
      <DashboardContainer>
        <SubtitleContainer>
          <Icon
            color='#9E9E9E'
            icon='group'
            size='24px'
          />
          <Text
            styledTextProps={{
              fontSize: '19px',
              fontWeight: '700',
              marginLeft: '10px',
            }}
          >
            Group Analysis
          </Text>
        </SubtitleContainer>
        <OptionsHeader>
          <MenuOptionsContainer>
            <DropDown
              defaultOption={group || {}}
              disabled
              label='name'
              list={['']}
              headerWidthPx={220}
              placeholder='Select a Group'
              readOnly
              selectItemFunction={emptyFunction}
            />
            <DropDown
              defaultOption={selectedExercise || {}}
              disabled={!group?.id}
              label='name'
              list={exercises}
              headerWidthPx={220}
              placeholder='Select an Exercise'
              selectItemFunction={setExercise}
            />
            <ThresholdFilter />
          </MenuOptionsContainer>
          <MenuOptionsContainer>
            <DatePicker
              datesWithData={datesWithData}
              maxDate={endDateData}
              minDate={startDate}
              setStartDate={handleEndDate}
              startDate={selectedEndDateAnalysis || null}
              styledWrapperProps={{
                alignSelf: 'center',
                customBorder: '1px solid #D8D8D8',
                customHeight: '42px',
              }}
            />
          </MenuOptionsContainer>
        </OptionsHeader>
        <ColorNote>
          <span style={{ fontWeight: 'bold' }}>NOTE: </span>
          The column background color is determined by the threshold set.
          The column will be red if the athlete is below the threshold and
          green if the athlete is within the threshold.
        </ColorNote>
        {isVBTDataLoading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : !hasTableData ? (
          <>
            <VBTAnalysisTableNoData
              dataRows={['', '', '', '', '']}
            />
            <NoDataModal>
              <ModalTitle>
                No Data Available
              </ModalTitle>
              <ModalMessage>
                There is no data available, please adjust your selection above
              </ModalMessage>
            </NoDataModal>
          </>
        ) : (
          <VBTAnalysisTable
            dataRows={tableData}
            groupData={groupData}
            threshold={tableThreshold}
          />
        )}
      </DashboardContainer>
    </>
  );
};

ViewOnlyAnalysis.propTypes = {
  token: PropTypes.string.isRequired,
};

export default ViewOnlyAnalysis;
